// R-13: ImageSourcePage - 메인 오케스트레이터 (상태 관리는 useAutomationSetup 훅으로 분리)
import { useAuth } from '../../context/AuthContext';
import { useAutomationSetup } from './hooks/useAutomationSetup';
import OAuthGuideModal from './components/OAuthGuideModal';
import AccountSetupStep from './components/AccountSetupStep';
import ContentSettingsStep from './components/ContentSettingsStep';
import SchedulerStep from './components/SchedulerStep';

function ImageSourcePage() {
  const { user } = useAuth();
  const setup = useAutomationSetup(user);

  return (
    <div style={{ minHeight: '100vh', background: '#101022' }}>
      {/* Instagram 연결 사전 준비 가이드 모달 */}
      {setup.showOAuthGuide && (
        <OAuthGuideModal
          onClose={() => setup.setShowOAuthGuide(false)}
          onStart={setup.startOAuthFlow}
        />
      )}

      <div className="max-w-[1280px] mx-auto px-4 py-6 sm:px-8 sm:py-10">
        {/* 헤더 */}
        <div className="flex flex-col sm:flex-row sm:flex-wrap sm:justify-between sm:items-end gap-4 sm:gap-6 mb-6 sm:mb-10">
          <div className="flex flex-col gap-1 sm:gap-2">
            <h2 className="text-2xl sm:text-4xl font-black tracking-tight">
              자동화 설정 대시보드
            </h2>
            <p className="text-slate-400 text-sm sm:text-lg">
              {setup.isEditMode
                ? '설정을 수정한 후 하단의 저장 버튼을 눌러주세요.'
                : '인스타그램 자동 포스팅 워크플로우를 구성하세요.'}
            </p>
          </div>
        </div>

        {/* 에러/성공 메시지 */}
        {setup.error && (
          <div style={{
            background: '#fee2e2', color: '#991b1b', padding: '16px',
            borderRadius: '12px', marginBottom: '20px', position: 'relative'
          }}>
            {setup.error}
            <button
              onClick={() => setup.setError(null)}
              style={{
                position: 'absolute', right: '16px', top: '16px',
                background: 'none', border: 'none', fontSize: '20px',
                cursor: 'pointer', color: '#991b1b'
              }}
            >
              ✕
            </button>
          </div>
        )}

        {setup.success && (
          <div style={{
            background: '#d1fae5', color: '#065f46', padding: '16px',
            borderRadius: '12px', marginBottom: '20px', position: 'relative'
          }}>
            {setup.success}
            <button
              onClick={() => setup.setSuccess(null)}
              style={{
                position: 'absolute', right: '16px', top: '16px',
                background: 'none', border: 'none', fontSize: '20px',
                cursor: 'pointer', color: '#065f46'
              }}
            >
              ✕
            </button>
          </div>
        )}

        {/* 진행 상태 바 */}
        <div style={{
          marginBottom: '48px', padding: '24px', background: '#111118',
          borderRadius: '12px', border: '1px solid #3b3b54'
        }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between',
            alignItems: 'center', marginBottom: '12px'
          }}>
            <span style={{
              fontSize: '14px', fontWeight: '600', textTransform: 'uppercase',
              letterSpacing: '0.05em', color: '#1313ec'
            }}>
              자동화 설정
            </span>
          </div>
        </div>

        {/* 1단계: 인스타그램 계정 설정 */}
        <AccountSetupStep
          selectedAccount={setup.selectedAccount}
          accounts={setup.accounts}
          isUserRole={setup.isUserRole}
          handleInstagramConnect={setup.handleInstagramConnect}
          handleDisconnectAccount={setup.handleDisconnectAccount}
          setSelectedAccount={setup.setSelectedAccount}
          setCurrentStep={setup.setCurrentStep}
        />

        {/* 2단계: 콘텐츠 설정 */}
        <ContentSettingsStep
          contentSettings={setup.contentSettings}
          setContentSettings={setup.setContentSettings}
          hashtags={setup.hashtags}
          hashtagInput={setup.hashtagInput}
          setHashtagInput={setup.setHashtagInput}
          addHashtag={setup.addHashtag}
          removeHashtag={setup.removeHashtag}
          contentPillars={setup.contentPillars}
          addContentPillar={setup.addContentPillar}
          removeContentPillar={setup.removeContentPillar}
          updateContentPillar={setup.updateContentPillar}
          aiSuggestionLoading={setup.aiSuggestionLoading}
          handleAiProfileSuggestion={setup.handleAiProfileSuggestion}
          customPrompt={setup.customPrompt}
          setCustomPrompt={setup.setCustomPrompt}
          isCustomPrompt={setup.isCustomPrompt}
          showAdvancedPrompt={setup.showAdvancedPrompt}
          setShowAdvancedPrompt={setup.setShowAdvancedPrompt}
          saveCustomPrompt={setup.saveCustomPrompt}
          resetCustomPrompt={setup.resetCustomPrompt}
        />

        {/* 3단계: 스케줄러 설정 */}
        <SchedulerStep
          selectedDays={setup.selectedDays}
          imageGenDays={setup.imageGenDays}
          setImageGenDays={setup.setImageGenDays}
          uploadTime={setup.uploadTime}
          setUploadTime={setup.setUploadTime}
          postsPerDay={setup.postsPerDay}
          setPostsPerDay={setup.setPostsPerDay}
          toggleDay={setup.toggleDay}
        />

        {/* 하단 여백 (플로팅 버튼 공간) */}
        <div style={{ height: '120px' }} />
      </div>

      {/* 플로팅 저장 버튼 */}
      {setup.currentStep >= 2 && (
        <div className="fixed bottom-0 left-0 right-0 z-50 lg:left-64">
          <div className="max-w-[1280px] mx-auto px-4 sm:px-8 pb-6 pt-4"
               style={{ background: 'linear-gradient(to top, #101022 60%, transparent)' }}>
            <div className="flex flex-col items-center gap-2">
              <button
                onClick={setup.handleStartAutomation}
                disabled={setup.loading}
                className="w-full sm:w-auto px-12 py-4 rounded-full font-black text-lg text-white flex items-center justify-center gap-3 transition-all shadow-2xl disabled:cursor-not-allowed"
                style={{
                  background: setup.loading ? '#94a3b8' : '#1313ec',
                  boxShadow: '0 25px 50px -12px rgba(19, 19, 236, 0.4)',
                }}
              >
                <span>{setup.isEditMode ? '💾' : '▶'}</span>
                {setup.loading ? '저장 중...' : (setup.isEditMode ? '설정 저장' : '자동화 시작')}
              </button>
              <p className="text-xs text-slate-500">
                {setup.isEditMode
                  ? '변경된 설정이 즉시 적용됩니다.'
                  : '시작 시 서비스 이용약관에 동의하는 것으로 간주됩니다.'}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ImageSourcePage;
